// dependencies
import { cva } from "class-variance-authority";
import * as React from "react";

import { cn } from "../../../lib/utils";

import { PopIn } from "../../organisms/AnimatePresence";
// components
import { Tooltip } from "../Tooltip";

export type BadgeSize = 8 | 12 | 16 | 24;
export const DEFAULT_BADGE_SIZE: BadgeSize = 24;

export interface BadgeProps {
	/**
	 * If true, the badge will have a border
	 */
	border?: boolean;
	/**
	 * The size of the badge in pixels
	 * @default 24
	 */
	size?: BadgeSize;
	/**
	 * How the badge is positioned.
	 * - "attached": absolutely positioned over the top-right corner of the parent element (default)
	 * - "inline": participates in normal layout flow, for use alongside text or other content
	 * @default "attached"
	 */
	placement?: "attached" | "inline";
	/**
	 * The tooltip to display when hovering over the badge
	 */
	tooltip?: string | React.ReactNode;
	/**
	 * A class name to apply to the badge
	 */
	dangerouslySetClassName?: string;
	/**
	 * The content to display in the badge
	 */
	children: React.ReactNode;
	/**
	 * If true, the badge will animate in/out with a scale/pop effect when added/removed from the DOM.
	 * Use with AnimatePresence for exit animations.
	 * @default false
	 */
	animateEnter?: boolean;
}

const badgeVariants = cva("flex items-center justify-center rounded-full bg-neutral-2 text-neutral-9", {
	variants: {
		border: {
			true: "border border-solid border-neutral-0",
		},
		size: {
			8: "size-2 text-[4px] leading-[5px]",
			12: "size-3 text-[6px] leading-[6px]",
			16: "size-4 text-[8px] leading-[8px]",
			24: "size-6 text-caption",
		},
	},
	defaultVariants: {
		border: false,
		size: DEFAULT_BADGE_SIZE,
	},
});

const positionBySize: Record<BadgeSize, string> = {
	8: "absolute -right-1 -top-1",
	12: "absolute -right-1.5 -top-1.5",
	16: "absolute -right-2 -top-2",
	24: "absolute -right-4 -top-3",
};

/**
 * The badge component is used to display the count of items. To use it, include it as a child of the component you want to attach it to.
 */
const Badge = (props: BadgeProps) => {
	const {
		children,
		border,
		size,
		dangerouslySetClassName,
		tooltip,
		placement = "attached",
		animateEnter = false,
	} = props;

	const badgeClasses = cn(badgeVariants({ border, size }), `${dangerouslySetClassName}`);

	const badgeContent = animateEnter ? (
		<PopIn dangerouslySetClassName={badgeClasses}>{children}</PopIn>
	) : (
		<div className={badgeClasses}>{children}</div>
	);

	return (
		<div
			className={
				placement === "inline" ? "flex shrink-0 items-center" : positionBySize[size ?? DEFAULT_BADGE_SIZE]
			}
		>
			{tooltip ? (
				<Tooltip content={tooltip} asChild>
					{badgeContent}
				</Tooltip>
			) : (
				badgeContent
			)}
		</div>
	);
};

export { Badge };
